import { apm } from "@elastic/apm-rum";
import { mm } from "@inovitas/infra3dsdk";
import { Box, Typography } from "@mui/material";
import React, { DragEvent, useState } from "react";
import { useTranslation } from "react-i18next";

import uploadFileImage from "shared/assets/uploadFileImage.png";
import { monitor } from "shared/common/services/apm-logging";
import { defaultStore } from "shared/common/stores/defaultStore";
import {
  sidebarStore,
  WebServiceLayerType,
} from "shared/common/stores/sidebarStore";
import { addLayers } from "shared/common/utils/layers/addLayers";
import {
  getLayersAddedByUser,
  saveLayersToLocalStorage,
} from "shared/common/utils/layers/localStorageLayers";
import ClientConfig from "../../../../../../config";
import { v4 as uuidv4 } from "uuid";

export function DropLayer() {
  const { t } = useTranslation();

  const viewer = defaultStore((state) => state.viewer);
  const darkTheme = defaultStore((state) => state.darkTheme);
  const changeLoading = defaultStore((state) => state.changeLoading);
  const changeAlertMessage = defaultStore((state) => state.changeAlertMessage);

  const changeExpandAddLayersList = sidebarStore(
    (state) => state.changeExpandAddLayersList
  );
  const changeMaximizeTable = sidebarStore(
    (state) => state.changeMaximizeTable
  );

  const [isOver, setIsOver] = useState(false);

  // Define the event handlers
  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsOver(true);
  };

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsOver(false);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsOver(false);

    changeLoading(true);
    const input = document.createElement("input");
    input.type = "file";
    input.name = "datafile";
    input.files = event.dataTransfer?.files;

    // Fetch the files
    importFromFile(input);
  };

  //import layers from file
  const importFromFile = (input: HTMLInputElement) => {
    if (!viewer) return;
    mm.importGeoJSONFromFile(
      input,
      ClientConfig.conversionURL,
      viewer.authObject.apiKey
    )
      .then((data) => {
        if (!data || data.length === 0) {
          changeAlertMessage({
            message: t("alert_error.failed_converting_file"),
            type: "error",
          });
          return;
        }
        data.forEach((layer: any) => {
          // TODO: Type
          addNewMemoryLayer({
            addedFromUser: true,
            ...layer,
            geometrytype: mm.Geometry.toNumber(
              layer.data?.features[0].geometry.type
            ),
            id: layer.id || uuidv4(),
          });
        });
      })
      .catch((error) => {
        const errorMessage = error?.message || "";
        let layerUploadErrorMessage = errorMessage;

        if (errorMessage.includes("Could not upload the file.")) {
          layerUploadErrorMessage = t("alert_error.failed_converting_file");
        } else if (errorMessage.includes("notsupportedformat")) {
          layerUploadErrorMessage = t("alert_error.wrong_format");
        }

        changeAlertMessage({
          message: layerUploadErrorMessage,
          type: "error",
        });
      })
      .finally(() => {
        changeLoading(false);
        changeExpandAddLayersList(false);
        changeMaximizeTable(false);
      });
  };

  //upload files
  const uploadFiles = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.name = "datafile";
    input.setAttribute("multiple", "");
    input.accept = ".geojson,.json,.shp,.zip,.dxf,.gml,.kml";
    input.click();
    input.onchange = () => {
      changeLoading(true);
      importFromFile(input);
    };
  };

  // "Drag'n'Drop GeoJSON oder SHP Upload" Button
  //add new layers to localstorage
  const addLayerFromUser = (layer: WebServiceLayerType) => {
    const allLayersAddedFromUser = getLayersAddedByUser();
    allLayersAddedFromUser.push(layer);
    saveLayersToLocalStorage(
      allLayersAddedFromUser,
      t("alert_success.file_drop"),
      t("add_layer.localstorage_quota_exceeded")
    );
  };

  //add new layer
  const addNewMemoryLayer = (layer: mm.ILayerOptions) => {
    const source = {
      layers: [layer],
      name: layer.layername,
      type: "memory",
    };
    if (viewer) {
      addLayers(viewer, [source]);
    }
    addLayerFromUser(source);
  };

  return (
    <Box
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={() => monitor(apm, "click-upload-layer | Add", uploadFiles)}
      sx={{
        position: "relative",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        gap: 1,
        justifyContent: "center",
        alignItems: "center",
        padding: 2,
        color: isOver
          ? darkTheme
            ? "darkblue.main"
            : "lightgray"
          : darkTheme
            ? "white"
            : "darkblue.main",
        border: isOver
          ? darkTheme
            ? "2px #053952 dotted"
            : "2px white dotted"
          : darkTheme
            ? "2px white dotted"
            : "2px #053952 dotted",
        borderRadius: "16px",
        backgroundColor: isOver
          ? darkTheme
            ? "lightgray"
            : "darkblue.main"
          : darkTheme
            ? "darkblue.main"
            : "white",
        "@media (hover: hover)": {
          ":hover": {
            cursor: "pointer",
            color: "secondary.main",
            borderColor: "secondary.main",
          },
        },
      }}
    >
      <img
        width="140px"
        alt="Designed by pch.vector / Magnific"
        src={uploadFileImage}
      />
      <Typography
        sx={{
          fontSize: 16,
        }}
      >
        {t("manage_layers.create_description_4")}
      </Typography>
    </Box>
  );
}
